import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import AccessDeniedView from 'components/AccessDeniedView'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import { SplitView, SplitViewMain, SplitViewSide } from 'components/SplitView'
import { TabBar, TabItem } from 'components/TabBar'
import { useDocumentTitle } from 'hooks/document-title'
import { useActivation } from 'hooks/mobx-hooks'
import MembershipsSidebar from 'user-management/components/MembershipsSidebar'
import UserDetailsInformation from 'user-management/components/UserDetailsInformation'
import { match } from 'utils/pattern-match-util'
import { formatUserDataType } from '../../formatters/user-data-type-formatter'
import MembershipsTab from '../MembershipsTab'
import DocumentsTab from '../DocumentsTab'
import JobsTab from '../JobsTab'
import { AddUserToTeamDialog } from '../AddUserToTeamDialog'
import { AddUserToWorkspaceDialog } from '../AddUserToWorkspaceDialog'
import { AddCustomerToWorkspaceDialog } from '../AddCustomerToWorkspaceDialog'
import { JoinWorkspaceAsAdminDialog } from '../JoinWorkspaceAsAdminDialog'
import EditUserInformationDialog from '../EditUserInformation'
import EditProDetailsDialog from '../EditProDetails'
import EditUserRoleDialog from '../EditUserRoleDialog'
import RemoveFromWorkspaceDialog from '../RemoveFromWorkspaceDialog'
import EditUserCapacityDialog from '../ManageUserCapacityDialog'
import UserInfoItemListDialog from '../UserInfoItemListDialog'
import { UserDetailsViewModel, UserDetailsTab } from './UserDetailsViewModel'
import { ManageSkillsListDialog } from '../ManageSkillsListDialog'
import RestoreToWorkspaceDialog from '../RestoreToWorkspaceDialog'
import PromoteToOwnerDialog from '../PromoteToOwnerDialog'
import ContentLoading from 'components/ContentLoading'
import styles from './UserDetails.sass'

const { isSupported, copy } = require('clipbrd')

interface UserDetailsProps {
  vm: UserDetailsViewModel
}

export default observer(({ vm }: UserDetailsProps) => {
  useDocumentTitle('User Details')
  useActivation(vm)
  const isSidebarOpen = vm.membershipsTabVM.isSidebarOpen

  if (!vm.canAccessUserDetails) {
    return <AccessDeniedView />
  }

  if (!vm.isUserInitialized) {
    return <ContentLoading />
  }

  return (
    <div className={styles.root}>
      <div className={cx(styles.main, isSidebarOpen && styles.narrow)}>
        <UserDetailsHeader vm={vm} />
        <UserDetailsTabs vm={vm} />
        <SplitView flexChild>
          <SplitViewSide narrow>
            <div className={styles.userInfoRoot}>
              <div className={styles.userInfoScrollContainer}>
                <UserDetailsInformation vm={vm} />
              </div>
            </div>
          </SplitViewSide>
          <SplitViewMain>
            <div className={styles.userTabRoot}>
              {match(vm.tab, {
                [UserDetailsTab.Memberships]: () => (
                  <MembershipsTab vm={vm.membershipsTabVM} />
                ),
                [UserDetailsTab.Jobs]: () => <JobsTab vm={vm.jobsTabVM} />,
                [UserDetailsTab.Documents]: () => (
                  <DocumentsTab vm={vm.documentsTabVM} />
                ),
              })}
            </div>
          </SplitViewMain>
        </SplitView>
        <AddUserToTeamDialog vm={vm.addUserToTeamVM} />
        <AddUserToWorkspaceDialog vm={vm.addUserToWorkspaceVM} />
        <AddCustomerToWorkspaceDialog vm={vm.addCustomerToWorkspaceVM} />
        <JoinWorkspaceAsAdminDialog vm={vm.joinWorkspaceAsAdminVM} />
        <EditUserInformationDialog vm={vm.editUserInformationVM} />
        <EditProDetailsDialog vm={vm.editProDetailsVM} />
        <EditUserRoleDialog vm={vm.editUserRoleVM} />
        <RemoveFromWorkspaceDialog vm={vm.removeFromWorkspaceVM} />
        <EditUserCapacityDialog vm={vm.manageUserCapacityVM} />
        <UserInfoItemListDialog vm={vm.userInfoItemListVM} />
        <ManageSkillsListDialog vm={vm.manageSkillListVM} />
        <RestoreToWorkspaceDialog vm={vm.restoreToWorkspaceVM} />
        <PromoteToOwnerDialog vm={vm.promoteToOwnerVM} />
      </div>
      <div className={cx(styles.sidebar, isSidebarOpen && styles.show)}>
        <MembershipsSidebar vm={vm.membershipsTabVM} />
      </div>
    </div>
  )
})

interface UserDetailsHeaderProps {
  vm: UserDetailsViewModel
}

const UserDetailsHeader = observer(({ vm }: UserDetailsHeaderProps) => {
  const user = vm.user

  const copyEmailToClipboard = (email: string) => {
    if (isSupported()) {
      copy(email)
    }
  }

  return (
    <div className={styles.header}>
      <div className={styles.headerInfo}>
        <Avatar size={48} subject={{ avatar: user.avatar }} />
        <div className={styles.headerInfoText}>
          <h1 className={styles.headerInfoTitle}>{user.displayName}</h1>
          <div className={styles.headerInfoSubtitle}>
            <h5
              className={cx(
                styles.headerInfoUserType,
                styles[user.userDataType.toLowerCase()],
              )}
            >
              {formatUserDataType(user.userDataType)}
            </h5>
            <span className={styles.headerInfoDivider}>|</span>
            <p className={styles.headerInfoEmail}>{user.email}</p>
            <Icon
              className={styles.headerInfoCopyIcon}
              size={16}
              name="copy"
              onClick={() => copyEmailToClipboard(user.email)}
            />
          </div>
        </div>
      </div>
      <div className={styles.headerActions}>
        <Dropdown
          autoDismiss
          position="bottom-end"
          button={
            <Button type="secondary" dataTest="user-details-actions">
              Actions
              <Icon size={14} name="dropdown" color="var(--grey600)" />
            </Button>
          }
        >
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem
            onClick={() => vm.addUserToTeamVM.show()}
            dataTest="user-details-actions-add-to-team"
          >
            Add user to team
          </DropdownItem>
          <DropdownItem
            onClick={() => vm.addUserToWorkspaceVM.show()}
            dataTest="user-details-actions-add-to-workspace"
          >
            Add user to workspace
          </DropdownItem>
          {vm.canCreateJobForUser && (
            <DropdownItem
              onClick={() => vm.createJob()}
              dataTest="user-details-actions-create-job"
            >
              Create job for user
            </DropdownItem>
          )}
          {vm.availableWorkspaces.length > 0 && (
            <DropdownItem
              onClick={() =>
                vm.editUserInformationVM.open(
                  vm.user,
                  vm.availableWorkspaces[0],
                )
              }
              dataTest="user-details-actions-edit-user-information"
            >
              Edit user information
            </DropdownItem>
          )}
        </Dropdown>
      </div>
    </div>
  )
})

interface UserDetailsTabsProps {
  vm: UserDetailsViewModel
}

const UserDetailsTabs = observer(({ vm }: UserDetailsTabsProps) => {
  const userDetailsTabs = Object.values(UserDetailsTab)

  return (
    <div className={styles.tabs}>
      <TabBar>
        {userDetailsTabs.map((tab) => (
          <TabItem
            key={tab}
            active={vm.tab === UserDetailsTab[tab]}
            onClick={() => vm.setTab(UserDetailsTab[tab])}
            dataTest={`user-details-tab-${tab.toLowerCase()}`}
          >
            {tab}
          </TabItem>
        ))}
      </TabBar>
    </div>
  )
})
